/* eslint-disable max-lines */
/* eslint-disable no-restricted-imports */
// FSD VIOLATION: Tab state management needs artwork and composition form data.
// Core state hook used across all layers - cannot be moved without breaking architecture.
import { useAtom, useSetAtom } from "jotai";
import { CloudUpload, PaintBucket, Palette, Ruler } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import {
  useArtworkForm,
  usePlacementPosition,
  mobileSizePlacementOpenAtom
} from "@design-lab/entities/artwork";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { trackTabNavigation } from "@design-lab/shared/lib/analytics";
import type { Tab, DesignSubTab, AppTab } from "@design-lab/shared/types/tab";
import { designSubTabAtom, tabInfoExpandedAtom } from "@design-lab/shared/lib/tab-navigation";
import DesignIcon from "assets/icons/design.svg";
import DesignSelectedIcon from "assets/icons/design-selected.svg";
import SizesIcon from "assets/icons/sizes.svg";
import SizesSelectedIcon from "assets/icons/sizes-selected.svg";
import ProductsIcon from "assets/icons/products.svg";
import ProductsSelectedIcon from "assets/icons/products-selected.svg";

export const APP_TABS_BASE: AppTab[] = [
  {
    title: "Upload",
    mobileTitle: "Upload",
    mobileAccordionTitle: "Upload",
    value: "upload",
    canBeSkipped: () => false,
    icon: <CloudUpload className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isFileSelected }) => isFileSelected,
    isChecked: ({ isFileSelected }) => isFileSelected
  },
  {
    title: "Products",
    mobileTitle: "Products",
    mobileAccordionTitle: "Modify Products & Colors",
    value: "products",
    canBeSkipped: () => false,
    icon: <PaintBucket className="size-6" />,
    isEnabled: ({ isFileSelected }) => isFileSelected,
    isValid: () => true,
    isChecked: ({ visitedTabs }) => visitedTabs.has("products")
  },
  {
    title: "Select Sizes",
    mobileTitle: "Sizes",
    mobileAccordionTitle: "Edit Sizes",
    value: "size",
    canBeSkipped: () => false,
    icon: <Ruler className="size-6" />,
    isEnabled: ({ isFileSelected }) => isFileSelected,
    isValid: ({ isQuantityValid }) => isQuantityValid,
    isChecked: ({ isQuantityValid }) => isQuantityValid
  },
  {
    title: "Design",
    mobileTitle: "Design",
    mobileAccordionTitle: "Design",
    value: "design",
    canBeSkipped: () => false,
    icon: <Palette className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isAtLeastOneArtworkProcessed }) => isAtLeastOneArtworkProcessed,
    isChecked: ({ visitedTabs, isFrontArtworkUploaded, isBackArtworkUploaded }) =>
      (visitedTabs.has("front") || visitedTabs.has("design")) &&
      (isFrontArtworkUploaded || isBackArtworkUploaded)
  }
];

const TAB_ORDER: Tab[] = ["upload", "design", "products", "size"];

function getOrderedTabs(): AppTab[] {
  const tabsMap = new Map(APP_TABS_BASE.map(tab => [tab.value, tab]));

  const ordered = TAB_ORDER.map(value => tabsMap.get(value)).filter((tab): tab is AppTab =>
    Boolean(tab)
  );
  return ordered.length === TAB_ORDER.length ? ordered : APP_TABS_BASE;
}

export function useAppTabs(): AppTab[] {
  return useMemo(() => getOrderedTabs(), []);
}

// Export APP_TABS for backward compatibility
// Components should use useAppTabs() hook instead
export const APP_TABS: AppTab[] = APP_TABS_BASE;

export function useTab() {
  const { lens, form: artworkForm } = useArtworkForm();
  const { isDesktop } = useResponsive();
  const currentPlacementLens = lens.focus("currentPlacement");
  const selectedFrontPlacementLens = lens.focus("selectedFrontPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);
  const selectedFrontPlacement = usePlacementPosition(selectedFrontPlacementLens);

  // Extract stable values to avoid re-renders
  const currentPlacementValue = currentPlacement.placement;
  const selectedFrontPlacementValue = selectedFrontPlacement.placement;
  const { setPlacement } = currentPlacement;

  const validation = useValidation();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};
  const setIsSizePlacementOpen = useSetAtom(mobileSizePlacementOpenAtom);
  const [activeSubTab] = useAtom(designSubTabAtom);

  const [isInfoExpanded, setIsInfoExpanded] = useAtom(tabInfoExpandedAtom);
  const {
    tab,
    setTab: setTabNav,
    currentIndex,
    nextTab,
    nextTabRaw,
    prevTab,
    prevTabRaw,
    canBeSkipped,
    canGoNext,
    canGoBack,
    isDesignTab,
    isProductsTab,
    isSizeTab,
    isSample
  } = useTabNavigation({ artworkForm, ...(sides && { sides }) });

  const appTabs = useAppTabs();

  const tabs: AppTab[] = useMemo(() => {
    const validationWithSides = { ...validation, ...(sides && { sides }) };
    return appTabs.map(item => {
      let { title, mobileTitle, icon } = item;

      // Sample mode transformation
      if (isSample && item.value === "products") {
        title = "Colors";
        mobileTitle = "Colors";
      }

      if (item.value === "size") {
        title = "Sizes";
      }

      const ICON_META = {
        design: { normal: DesignIcon, selected: DesignSelectedIcon, label: "Design" },
        size: { normal: SizesIcon, selected: SizesSelectedIcon, label: "Sizes" },
        products: { normal: ProductsIcon, selected: ProductsSelectedIcon, label: "Products" }
      } as const;

      if (item.value in ICON_META) {
        const isSelected = item.value === tab;
        const color = isSelected ? "#155DFC" : "#171717";
        const meta = ICON_META[item.value as keyof typeof ICON_META];
        const IconComponent = isSelected ? meta.selected : meta.normal;

        icon = (
          <IconComponent
            className="size-6"
            style={{ fill: "none", stroke: color }}
            aria-label={meta.label}
          />
        );
      }

      return {
        ...item,
        title,
        mobileTitle,
        icon,
        valid: item.isValid?.(validationWithSides) ?? false,
        checked: item.isChecked?.(validationWithSides) ?? false,
        enabled: item.isEnabled?.(validationWithSides) ?? false
      };
    });
  }, [appTabs, isSample, validation, sides, tab]);

  const currentTabFromTabs = tabs.find(t => t.value === tab);
  const isValid = currentTabFromTabs?.valid ?? tabs[currentIndex]?.valid;

  const setTab = (value: Tab) => {
    setTabNav(value);

    let targetPlacement: "back" | typeof selectedFrontPlacement.placement | null = null;

    // Handle direct front/back tabs
    if (value === "front") {
      targetPlacement = selectedFrontPlacement.placement;
    } else if (value === "back") {
      targetPlacement = "back";
    }
    // Sync placement with activeSubTab for tabs with sub-tab UI
    // activeSubTab is global state tracking which sub-tab (front/back) is selected
    else if (value === "design" || value === "upload") {
      targetPlacement = activeSubTab === "front" ? selectedFrontPlacement.placement : "back";
    }

    if (targetPlacement) {
      currentPlacement.setPlacement(targetPlacement);
    }
  };

  // Only sync placement when tab or activeSubTab actually changes, not when placement changes
  const lastSyncedRef = useRef({ tab: "", activeSubTab: "" as DesignSubTab });

  useEffect(() => {
    // Check if tab or activeSubTab actually changed
    const hasChanged =
      lastSyncedRef.current.tab !== tab || lastSyncedRef.current.activeSubTab !== activeSubTab;

    if (!hasChanged) {
      return;
    }

    // Update ref to mark as processed
    lastSyncedRef.current = { tab, activeSubTab };

    // Sync placement with activeSubTab on design/upload tabs
    if (tab === "design" || tab === "upload") {
      const targetPlacement = activeSubTab === "front" ? selectedFrontPlacementValue : "back";

      if (currentPlacementValue !== targetPlacement) {
        setPlacement(targetPlacement);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activeSubTab]);

  const next = () => {
    setIsSizePlacementOpen(false);
    if (nextTabRaw) {
      setTab(nextTabRaw.value);
      trackTabNavigation(nextTabRaw.value, "next");
    }
  };

  const back = () => {
    setIsSizePlacementOpen(false);
    if (prevTabRaw) {
      setTab(prevTabRaw.value);
      trackTabNavigation(prevTabRaw.value, "tabs");
    }
  };

  const toggleInfoExpanded = () => {
    setIsInfoExpanded(!isInfoExpanded);
  };

  useEffect(() => {
    if (isInfoExpanded === undefined) {
      setIsInfoExpanded(isDesktop);
    }
  }, [isDesktop, isInfoExpanded, setIsInfoExpanded]);

  return {
    tabs,
    tab,
    setTab,
    nextTab,
    prevTab,
    next,
    back,
    canBeSkipped,
    canGoNext,
    canGoBack,
    isDesignTab,
    isValid,
    isProductsTab,
    isSizeTab,
    isInfoExpanded,
    toggleInfoExpanded
  };
}
